import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePlatform, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';
import {
  getCompleteAd3Url,
  getPlayContinueUrl,
  getPlayEndUrl,
  getPlayItemReuseUrl,
  getPlayStart2Url,
  getRecentPlayHistoryUrl,
  getStartAd3Url,
  getState2Url,
} from '../../api/generated/cat-runner/game-cat-runner/game-cat-runner.ts';
import CatRunningGameScreen from './components/CatRunningGameScreen';
import CatRunningLobbyScreen from './components/CatRunningLobbyScreen';
import CatRunningReadyScreen from './components/CatRunningReadyScreen';
import { CAT_RUNNER_PRE_ITEM_TYPES } from './mockBackend';
import { saveCatRunningReplay } from './replayStorage';
import magnetIcon from '../../assets/cat-running/items/magnet.png';
import shieldIcon from '../../assets/cat-running/items/shield.png';
import heartCounterIcon from '../../assets/cat-running/ui/icon-heart.png';
import snackCounterIcon from '../../assets/cat-running/ui/icon-snack-counter.png';

const CAT_RUNNING_ITEM_TOAST_MESSAGE = {
  [CAT_RUNNER_PRE_ITEM_TYPES.MAGNET]: '자석 아이템을 획득했어요!',
  [CAT_RUNNER_PRE_ITEM_TYPES.SHIELD]: '무적방패 아이템을 획득했어요!',
};

const CAT_RUNNING_ITEM_ACTIVATION_MESSAGE = {
  [CAT_RUNNER_PRE_ITEM_TYPES.MAGNET]: { icon: magnetIcon, label: '자석 활성!' },
  [CAT_RUNNER_PRE_ITEM_TYPES.SHIELD]: { icon: shieldIcon, label: '방패 활성!' },
};

const getResourceUseToastMessage = ({ icon, label, cost = 1, suffix } = {}) => (
  <span className="inline-flex items-center justify-center gap-1.5">
    <img src={icon} alt="" className="h-5 w-5 object-contain" />
    <span>{`${label} -${cost} ${label === '무료하트' ? '사용' : '소비'}!`}</span>
    {suffix ? (
      <>
        <span>/</span>
        <img src={suffix.icon} alt="" className="h-5 w-5 object-contain" />
        <span>{suffix.label}</span>
      </>
    ) : null}
  </span>
);

const getSnackUseToastMessage = (cost = 1, suffix) =>
  getResourceUseToastMessage({ icon: snackCounterIcon, label: '간식', cost, suffix });

const getSnackGainToastMessage = (amount) => (
  <span className="inline-flex items-center justify-center gap-1.5">
    <img src={snackCounterIcon} alt="" className="h-5 w-5 object-contain" />
    <span>{`간식 +${amount}개 획득!`}</span>
  </span>
);

const createRuntimeStateSnapshot = (payload) => ({
  balance: payload?.balance ?? 0,
  snackCount: payload?.snackCount ?? 0,
  heartsRemaining: payload?.heartsRemaining ?? 0,
  randomRefreshAvailable: payload?.randomRefreshAvailable ?? false,
  randomRefreshFreeAvailable: payload?.randomRefreshFreeAvailable ?? false,
  randomRefreshAdRequired: payload?.randomRefreshAdRequired ?? false,
  state: payload?.state ?? {},
  activeRound: payload?.activeRound ?? null,
});

const getArmedPreItemTypesFromState = (userState) => {
  const armed = [];
  if (Number(userState?.preItemMagnetArmed ?? 0) === 1) armed.push(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET);
  if (Number(userState?.preItemShieldArmed ?? 0) === 1) armed.push(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD);
  return armed;
};

export default function CatRunningPage() {
  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  const location = useLocation();
  const navigate = useNavigate();
  const browserDeviceType = usePlatform();
  const { showToast, showErrorToast } = useToast();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const [runtimeState, setRuntimeState] = useState(null);
  const [activeRound, setActiveRound] = useState(null);
  const [isPendingPreItemAd, setIsPendingPreItemAd] = useState(false);
  const [adLoadingItemType, setAdLoadingItemType] = useState(null);
  const [snackShortagePopup, setSnackShortagePopup] = useState(false);
  const [isPendingSnackChargeAd, setIsPendingSnackChargeAd] = useState(false);
  const pendingPreItemTypeRef = useRef(null);
  const pendingPreItemAdTokenRef = useRef(null);
  const pendingSnackChargeAdTokenRef = useRef(null);
  const pendingPreItemToastRef = useRef(null);
  const pendingSnackChargeToastRef = useRef(null);

  const fetcher = useCallback(async (...args) => fetchWithToken(...args), [fetchWithToken]);
  const { data: catRunnerStateResponse, mutate: mutateCatRunnerState } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getState2Url()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );
  const { data: recentPlayHistoryResponse, mutate: mutateRecentPlayHistory } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getRecentPlayHistoryUrl({ limit: 10 })}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  useEffect(() => {
    if (!catRunnerStateResponse) return;
    setRuntimeState(createRuntimeStateSnapshot(catRunnerStateResponse));
  }, [catRunnerStateResponse]);

  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);

  useEffect(() => {
    const showPendingAdToast = () => {
      [pendingPreItemToastRef, pendingSnackChargeToastRef].forEach((toastRef) => {
        const message = toastRef.current;
        if (!message) return;

        toastRef.current = null;
        window.setTimeout(() => showToast(message), 150);
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') showPendingAdToast();
    };

    window.addEventListener('focus', showPendingAdToast);
    window.addEventListener('pageshow', showPendingAdToast);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', showPendingAdToast);
      window.removeEventListener('pageshow', showPendingAdToast);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showToast]);

  const currentState = runtimeState ?? (catRunnerStateResponse ? createRuntimeStateSnapshot(catRunnerStateResponse) : null);
  const snackCount = currentState?.snackCount ?? 0;
  const heartsRemaining = currentState?.heartsRemaining ?? 0;
  const armedPreItemTypes = useMemo(() => getArmedPreItemTypesFromState(currentState?.state), [currentState?.state]);

  const syncRuntimeStateFromPayload = useCallback((payload) => {
    setRuntimeState((prev) => ({
      ...(prev ?? {}),
      ...createRuntimeStateSnapshot(payload),
    }));
  }, []);

  const handlePreItemAdError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingPreItemAd) return;
      pendingPreItemTypeRef.current = null;
      pendingPreItemAdTokenRef.current = null;
      setIsPendingPreItemAd(false);
      setAdLoadingItemType(null);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
      mutateCatRunnerState();
    },
    [isPendingPreItemAd, mutateCatRunnerState, showErrorToast],
  );

  useAdsListener('', '', { task: 'cat-runner-ready-pre-item', pending: isPendingPreItemAd, onError: handlePreItemAdError }, async () => {
    if (!isPendingPreItemAd || !accessToken) return;

    const itemType = pendingPreItemTypeRef.current;
    const adToken = pendingPreItemAdTokenRef.current;
    pendingPreItemTypeRef.current = null;
    pendingPreItemAdTokenRef.current = null;
    setIsPendingPreItemAd(false);
    setAdLoadingItemType(null);

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placement: 'preItem',
          itemType,
          adToken,
        }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '광고 보상 처리에 실패했어요.');
        mutateCatRunnerState();
        return;
      }

      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      const toastMessage = CAT_RUNNING_ITEM_TOAST_MESSAGE[itemType] || '아이템을 획득했어요!';
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        showToast(toastMessage);
      } else {
        pendingPreItemToastRef.current = toastMessage;
      }
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/complete', { error });
      showErrorToast('광고 보상 처리에 실패했어요.');
      mutateCatRunnerState();
    }
  });

  const handleSnackChargeAdError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingSnackChargeAd) return;
      pendingSnackChargeAdTokenRef.current = null;
      setIsPendingSnackChargeAd(false);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
    },
    [isPendingSnackChargeAd, showErrorToast],
  );

  useAdsListener('', '', { task: 'cat-runner-ready-snack-charge', pending: isPendingSnackChargeAd, onError: handleSnackChargeAdError }, async () => {
    if (!isPendingSnackChargeAd || !accessToken) return;

    const adToken = pendingSnackChargeAdTokenRef.current;
    pendingSnackChargeAdTokenRef.current = null;
    setIsPendingSnackChargeAd(false);

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placement: 'snackCharge', adToken }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '광고 보상 처리에 실패했어요.');
        mutateCatRunnerState();
        return;
      }

      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      setSnackShortagePopup(false);
      const grantedSnack = Number(payload?.grantedSnack ?? 0);
      if (grantedSnack > 0) {
        const toastMessage = getSnackGainToastMessage(grantedSnack);
        if (document.visibilityState === 'visible' && document.hasFocus()) {
          showToast(toastMessage);
        } else {
          pendingSnackChargeToastRef.current = toastMessage;
        }
      }
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/complete (snackCharge)', { error });
      showErrorToast('광고 보상 처리에 실패했어요.');
      mutateCatRunnerState();
    }
  });

  const handleArmPreItem = useCallback(
    async (itemType) => {
      if (!itemType) return;
      if (!accessToken) {
        showErrorToast('로그인 후 이용해 주세요.');
        return;
      }
      if (armedPreItemTypes.includes(itemType) || isPendingPreItemAd) return;

      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        showErrorToast('앱 광고 실행 핸들러를 찾지 못했어요.');
        return;
      }

      try {
        setAdLoadingItemType(itemType);
        const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            placement: 'preItem',
            itemType,
            deviceType,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          setAdLoadingItemType(null);
          showErrorToast(payload?.message || '광고를 불러오지 못했어요.');
          return;
        }

        pendingPreItemTypeRef.current = itemType;
        pendingPreItemAdTokenRef.current = payload?.adToken ?? null;
        setIsPendingPreItemAd(true);
        runAdmob('', payload?.placementId ?? '');
      } catch (error) {
        console.error('backend api: error POST /cat-runner/ads/start', { error });
        setAdLoadingItemType(null);
        showErrorToast('광고를 불러오지 못했어요.');
      }
    },
    [
      API_SERVER_DOMAIN,
      accessToken,
      armedPreItemTypes,
      deviceType,
      isPendingPreItemAd,
      showErrorToast,
    ],
  );

  const handleSnackCharge = useCallback(async () => {
    if (isPendingSnackChargeAd) return;
    if (!accessToken) {
      showErrorToast('로그인 후 이용해 주세요.');
      return;
    }
    if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
      showErrorToast('광고 재생 모듈을 찾을 수 없어요.');
      return;
    }

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placement: 'snackCharge', deviceType }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.result === 'fail' || !payload?.adToken) {
        showErrorToast(payload?.message || '광고를 불러오지 못했어요.');
        return;
      }

      pendingSnackChargeAdTokenRef.current = payload.adToken;
      setIsPendingSnackChargeAd(true);
      runAdmob('', payload?.placementId ?? '');
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/start (snackCharge)', { error });
      showErrorToast('광고를 불러오지 못했어요.');
    }
  }, [API_SERVER_DOMAIN, accessToken, deviceType, isPendingSnackChargeAd, showErrorToast]);

  const handleStartGame = useCallback(async () => {
    if (!accessToken) {
      showErrorToast('로그인 후 이용해 주세요.');
      return;
    }
    if (heartsRemaining <= 0 && snackCount <= 0) {
      setSnackShortagePopup(true);
      return;
    }

    const previousHeartsRemaining = heartsRemaining;
    const previousSnackCount = snackCount;

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getPlayStart2Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'SINGLE',
          preItems: armedPreItemTypes,
        }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail' || !payload?.roundToken) {
        showErrorToast(payload?.message || '캣러닝 시작에 실패했어요.');
        return;
      }

      setActiveRound({
        roundToken: payload.roundToken,
        mode: payload.mode ?? 'SINGLE',
        preItems: armedPreItemTypes,
        runSeed: payload.runSeed,
        spawnManifest: payload.spawnManifest,
        warmupUntilM: payload.warmupUntilM,
        continueRemaining: payload.continueRemaining,
        startedAt: payload.startedAt,
        expiresAt: payload.expiresAt,
        countdownRequired: payload.countdownRequired,
      });
      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      const usedHearts = Math.max(0, previousHeartsRemaining - Number(payload?.heartsRemaining ?? previousHeartsRemaining));
      const usedSnacks = Math.max(0, previousSnackCount - Number(payload?.snackCount ?? previousSnackCount));
      if (usedHearts > 0) {
        showToast(getResourceUseToastMessage({ icon: heartCounterIcon, label: '무료하트', cost: usedHearts }));
      } else {
        showToast(getSnackUseToastMessage(usedSnacks || 1));
      }
      navigate('/cat-running/play');
    } catch (error) {
      console.error('backend api: error POST /cat-runner/play/start', { error });
      showErrorToast('캣러닝 시작에 실패했어요.');
    }
  }, [
    API_SERVER_DOMAIN,
    accessToken,
    armedPreItemTypes,
    heartsRemaining,
    mutateCatRunnerState,
    navigate,
    snackCount,
    showErrorToast,
    showToast,
    syncRuntimeStateFromPayload,
  ]);

  const handleContinueRound = useCallback(
    async ({ deathDistanceM, deathChuruCount } = {}) => {
      if (!accessToken || !activeRound?.roundToken) {
        showErrorToast('이어하기 정보를 찾을 수 없어요.');
        return false;
      }

      const previousSnackCount = snackCount;

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayContinueUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roundToken: activeRound.roundToken,
            deathDistanceM,
            deathChuruCount,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          showErrorToast(payload?.message || '이어하기에 실패했어요.');
          return false;
        }

        syncRuntimeStateFromPayload(payload);
        setActiveRound((prev) => ({
          ...(prev ?? {}),
          roundToken: payload?.roundToken ?? prev?.roundToken,
          warmupUntilM: payload?.warmupUntilM ?? prev?.warmupUntilM,
          continueRemaining: payload?.continueRemaining ?? prev?.continueRemaining,
          countdownRequired: payload?.countdownRequired ?? prev?.countdownRequired,
        }));
        mutateCatRunnerState();
        const usedSnacks = Math.max(0, previousSnackCount - Number(payload?.snackCount ?? previousSnackCount));
        showToast(getSnackUseToastMessage(usedSnacks || 1));
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/continue', { error });
        showErrorToast('이어하기에 실패했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, activeRound?.roundToken, mutateCatRunnerState, showErrorToast, showToast, snackCount, syncRuntimeStateFromPayload],
  );

  const handleFinishRound = useCallback(
    async (replayPayload) => {
      if (!replayPayload?.playEndRequest || !accessToken) {
        showErrorToast('게임 종료 정보를 찾을 수 없어요.');
        return false;
      }

      saveCatRunningReplay(replayPayload);

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayEndUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(replayPayload.playEndRequest),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          showErrorToast(payload?.message || '게임 결과 정산에 실패했어요.');
          return false;
        }

        syncRuntimeStateFromPayload(payload);
        mutateCatRunnerState();
        mutateRecentPlayHistory();
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/end', { error });
        showErrorToast('게임 결과 정산에 실패했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, mutateCatRunnerState, mutateRecentPlayHistory, showErrorToast, syncRuntimeStateFromPayload],
  );

  const handleUseItem = useCallback(
    async (itemType) => {
      if (!accessToken || !activeRound?.roundToken) {
        showErrorToast('아이템 사용 정보를 찾을 수 없어요.');
        return false;
      }

      const previousSnackCount = snackCount;

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayItemReuseUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roundToken: activeRound.roundToken,
            itemType,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          showErrorToast(payload?.message || '아이템 사용에 실패했어요.');
          return false;
        }

        syncRuntimeStateFromPayload(payload);
        mutateCatRunnerState();
        const usedSnacks = Math.max(
          0,
          previousSnackCount - Number(payload?.snackCount ?? previousSnackCount),
        );
        const snackCost = Number(payload?.snackCost) || usedSnacks;
        if (snackCost > 0) {
          const activationMessage = CAT_RUNNING_ITEM_ACTIVATION_MESSAGE[payload?.itemType ?? itemType];
          showToast(getSnackUseToastMessage(snackCost, activationMessage));
        }
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/item/reuse', { error });
        showErrorToast('아이템 사용에 실패했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, activeRound?.roundToken, mutateCatRunnerState, showErrorToast, showToast, snackCount, syncRuntimeStateFromPayload],
  );


  const screen = (() => {
    if (location.pathname === '/cat-running/play') {
      return 'game';
    }

    if (location.pathname === '/cat-running/ready') {
      return 'ready';
    }

    return 'lobby';
  })();
  if (screen === 'game') {
    return (
      <CatRunningGameScreen
        onBack={() => navigate('/cat-running')}
        onContinueRound={handleContinueRound}
        onFinishRound={handleFinishRound}
        onUseItem={handleUseItem}
        snackCount={snackCount}
        activeRound={activeRound}
        activePreItemTypes={activeRound?.preItems ?? armedPreItemTypes}
        previewGameOver={location.search.includes('preview=gameover')}
      />
    );
  }

  if (screen === 'ready') {
    return (
      <CatRunningReadyScreen
        onBack={() => navigate('/cat-running')}
        onHome={() => navigate('/cat-running')}
        onStart={handleStartGame}
        armedPreItemTypes={armedPreItemTypes}
        onArmPreItem={handleArmPreItem}
        snackCount={snackCount}
        heartsRemaining={heartsRemaining}
        adLoadingItemType={adLoadingItemType}
        snackShortageOpen={snackShortagePopup}
        snackChargeLoading={isPendingSnackChargeAd}
        onCloseSnackShortage={() => setSnackShortagePopup(false)}
        onChargeSnack={handleSnackCharge}
      />
    );
  }

  return (
    <CatRunningLobbyScreen
      onBattle={() => navigate('/cat-running/battle')}
      onStart={() => navigate('/cat-running/ready')}
      onReplayTest={() => navigate('/cat-running/replay-test')}
      onRanking={() => navigate('/cat-running/ranking')}
      snackCount={snackCount}
      playHistory={recentPlayHistoryResponse?.list ?? []}
    />
  );
}
